import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { Menu, MenuButton, MenuItem, MenuList } from '@revelio/ui';

import type { Position } from '../../PageConfigProvider/types/item';
import styles from '../EditableGrid.module.css';

type ChartClipboardContextMenuTarget =
  | { type: 'chart'; itemId: string }
  | { type: 'ghost'; position: Position };

export type ChartClipboardContextMenuState = {
  x: number;
  y: number;
  target: ChartClipboardContextMenuTarget;
};

type ChartClipboardContextMenuProps = {
  menu: ChartClipboardContextMenuState | null;
  onAction: (action: 'copy' | 'cut' | 'paste') => void;
  onClose: () => void;
};

const CHART_ACTIONS = [
  { id: 'copy', label: 'Copy', shortcut: '⌘/Ctrl+C' },
  { id: 'cut', label: 'Cut', shortcut: '⌘/Ctrl+X' },
  { id: 'paste', label: 'Paste', shortcut: '⌘/Ctrl+V' },
] as const;

export const ChartClipboardContextMenu = ({
  menu,
  onAction,
  onClose,
}: ChartClipboardContextMenuProps) => {
  if (!menu) return null;

  const actions =
    menu.target.type === 'chart'
      ? CHART_ACTIONS
      : CHART_ACTIONS.filter((action) => action.id === 'paste');

  return (
    <Menu
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {createPortal(
        <MenuButton>
          <span
            className={styles.clipboardContextMenuAnchor}
            style={
              {
                '--clipboard-menu-left': `${menu.x}px`,
                '--clipboard-menu-top': `${menu.y}px`,
              } as CSSProperties
            }
          />
        </MenuButton>,
        document.body
      )}
      <MenuList
        aria-label="Chart actions"
        sideOffset={0}
        collisionPadding={8}
        minWidth="13rem"
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            trailing={action.shortcut}
            onClick={() => onAction(action.id)}
          >
            {action.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
